import { Modal } from '@lume/ui-components';

/**
 * KeyboardShortcutsModal — r7b §⌘/ canônico.
 *
 * Brief 2A V2.3 §1.2 — "Composer sem faixa de atalhos persistente. Atalhos
 * via ⌘/ modal."
 *
 * Listagem de atalhos canônicos de teclado da conversa em curso.
 * Abre via ⌘/ (Cmd/Ctrl + /) a partir do ConversationHeader MoreMenu.
 */
export function KeyboardShortcutsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  if (!open) return null;
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Atalhos de teclado"
      cancelLabel="Fechar"
      maxWidth="520px"
    >
      <div className="space-y-4 text-sm">
        <ShortcutGroup title="Composer">
          <Shortcut keys={['Cmd', 'Enter']} label="Enviar mensagem" />
          <Shortcut keys={['Tab']} label="Aceitar sugestão da IA" />
          <Shortcut keys={['Esc']} label="Recusar sugestão / Limpar rascunho" />
        </ShortcutGroup>

        <ShortcutGroup title="Conversa">
          <Shortcut keys={['Cmd', 'W']} label="Encerrar conversa" available={false} />
          <Shortcut keys={['Cmd', '⇧', 'P']} label="Pausar conversa" available={false} />
          <Shortcut keys={['Cmd', '⇧', 'T']} label="Transferir conversa" available={false} />
        </ShortcutGroup>

        <ShortcutGroup title="Navegação">
          <Shortcut keys={['Cmd', '/']} label="Abrir / fechar este painel" />
          <Shortcut keys={['Cmd', 'P']} label="Nova pendência" available={false} />
          {/* O Localizador Universal existe desde 14/Ago/2026 — ⌘K deixou de ser
              «fatia futura». Ele abre de qualquer tela (montado no AppShell). */}
          <Shortcut keys={['Cmd', 'K']} label="Busca rápida" />
        </ShortcutGroup>

        <p className="text-2xs text-text-secondary opacity-60">
          Atalhos marcados com ⬤ disponíveis em fatias futuras.
        </p>
      </div>
    </Modal>
  );
}

function ShortcutGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-2">
        {title}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Shortcut({
  keys,
  label,
  available = true,
}: {
  keys: string[];
  label: string;
  available?: boolean;
}): JSX.Element {
  return (
    <div className={`flex items-center justify-between gap-3 ${available ? '' : 'opacity-50'}`}>
      <span className={`text-sm ${available ? 'text-text-primary' : 'text-text-secondary'}`}>
        {!available && <span className="text-2xs mr-1.5 opacity-60">⬤</span>}
        {label}
      </span>
      <span className="flex items-center gap-0.5 shrink-0">
        {keys.map((k) => (
          <kbd
            key={k}
            className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded border border-border-subtle bg-surface-raised text-2xs font-mono text-text-secondary leading-none"
          >
            {k}
          </kbd>
        ))}
      </span>
    </div>
  );
}
